import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Orb } from '@sorare/core/src/components/Orb';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import { RevealPackCardsDialogFromReward } from '#src/components/commonCard/RevealPackCardsDialogFromReward/index.tsx';
import { useSeriesDailyClaim } from '#src/hooks/packs/useSeriesDailyClaim.ts';

import type { SeriesDailyClaimModule_task } from './__generated__/index.graphql.ts';

const PackImage = styled.img`
  width: 32px;
  height: 48px;
  object-fit: contain;
  transition: opacity 0.3s ease-in-out;
  .loading & {
    opacity: 0.6;
  }
`;

type Props = {
  task?: SeriesDailyClaimModule_task;
  refetch: () => Promise<unknown>;
  onClaimed?: () => void;
};

export const SeriesDailyClaimModule = ({ task, refetch, onClaimed }: Props) => {
  const commonGameSet = useCommonGameSet();
  const {
    loading,
    isClaimable,
    pack,
    onClaim,
    reward,
    packDialogOpen,
    setPackDialogOpen,
    claimMutationData,
    dailyTasksExpiresAt,
  } = useSeriesDailyClaim(task, onClaimed);

  return (
    <>
      <Orb
        showNotificationDot={true}
        onClick={onClaim}
        disabled={!isClaimable}
        loading={loading}
        colors={commonGameSet.colors.mainRGB}
        chipState={isClaimable ? 'default' : 'timeLeft'}
        label={
          isClaimable ? (
            <FormattedMessage defaultMessage="Free" />
          ) : (
            dailyTasksExpiresAt && (
              <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
            )
          )
        }
      >
        {!!pack?.pictureUrl && <PackImage src={pack.pictureUrl} alt="" />}
      </Orb>
      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

SeriesDailyClaimModule.fragments = {
  task: gql`
    fragment SeriesDailyClaimModule_task on Task {
      id
      ...useSeriesDailyClaim_task
    }
    ${useSeriesDailyClaim.fragments.task}
  ` as TypedDocumentNode<SeriesDailyClaimModule_task>,
};
